import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import {
  PurchaseMarketProvider,
  usePurchaseMarket,
  type PurchaseMarketService,
} from './PurchaseMarketContext'
import { StatsSection } from '../_shared/stubs'
import { PriceEvolution } from './components/sections/PriceEvolution'
import { PriceM2Evolution } from './components/sections/PriceM2Evolution'
import { PricePerRoom } from './components/sections/PricePerRoom'
import { PriceM2PerRoom } from './components/sections/PriceM2PerRoom'
import { Distribution } from './components/sections/Distribution'
import { MarketSummaryMetrics } from './components/sections/MarketSummaryMetrics'
import { Icon } from '../../atoms/Icon'
import { FilterChip, FilterDisplay, type FilterItem } from '../../molecules/FilterDisplay'
import {
  StatsPageContainer,
  StatsContent,
  HeaderSection,
  SectionsContainer,
  PlaceholderChart,
  FilterSection,
  FilterBar,
  LocationFilters,
  LocationBadge,
  AdvancedFiltersToggle,
  AdvancedFiltersPanel,
  AdvancedFiltersHeader,
  AdvancedFilters,
} from './PurchaseMarketSection.styles'
import { AnalysisTabContentContainer } from '../../molecules/SectionAnalysisStyles'

type Period = 'all' | 'last-12-months' | 'since-2022'

interface Section {
  id: string
  titleKey: string
  descriptionKey: string
  period: Period
}

const purchaseMarketSections: Section[] = [
  {
    id: 'price-m2-evolution',
    titleKey: 'market.purchase.sections.priceM2Evolution.title',
    descriptionKey: 'market.purchase.sections.priceM2Evolution.description',
    period: 'all',
  },
  {
    id: 'price-evolution',
    titleKey: 'market.purchase.sections.priceEvolution.title',
    descriptionKey: 'market.purchase.sections.priceEvolution.description',
    period: 'all',
  },
  {
    id: 'distribution',
    titleKey: 'market.purchase.sections.distribution.title',
    descriptionKey: 'market.purchase.sections.distribution.description',
    period: 'last-12-months',
  },
  {
    id: 'price-per-room',
    titleKey: 'market.purchase.sections.pricePerRoom.title',
    descriptionKey: 'market.purchase.sections.pricePerRoom.description',
    period: 'last-12-months',
  },
  {
    id: 'price-m2-per-room',
    titleKey: 'market.purchase.sections.priceM2PerRoom.title',
    descriptionKey: 'market.purchase.sections.priceM2PerRoom.description',
    period: 'last-12-months',
  },
]

interface PurchaseMarketContentProps {
  entity: any
  readOnly?: boolean
}

const PurchaseMarketContent: React.FC<PurchaseMarketContentProps> = ({
  entity,
  readOnly = false,
}) => {
  const { t } = useTranslation('market')
  const { filters, updateLocationType, updatePropertyType, getLocationDisplay } =
    usePurchaseMarket()
  const contentRef = useRef<HTMLDivElement>(null)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true)

  const getLocationTypeLabel = () => {
    switch (filters.locationType) {
      case 'postal_code':
        return t('market.common.filters.postalCode')
      case 'city':
        return t('market.common.filters.city')
      case 'district':
        return t('market.common.filters.district')
      default:
        return ''
    }
  }

  // const [sectionVisibility, setSectionVisibility] = useState<
  //   Record<string, boolean>
  // >(() => {
  //   const initialVisibility: Record<string, boolean> = {}
  //   purchaseMarketSections.forEach((section) => {
  //     initialVisibility[section.id] = true
  //   })
  //   return initialVisibility
  // })

  // const toggleSectionVisibility = (sectionId: string) => {
  //   setSectionVisibility((prev) => ({
  //     ...prev,
  //     [sectionId]: !prev[sectionId],
  //   }))
  // }

  return (
    <StatsPageContainer>
      <StatsContent ref={contentRef}>
        <HeaderSection>
          <AnalysisSectionHeader
            title={t('purchase.header.title')}
            subtitle={t('purchase.header.subtitle')}
          />
        </HeaderSection>

        <AnalysisTabContentContainer>
          {readOnly ? (
            // Read-only filter display for public view
            <FilterDisplay
              filters={
                [
                  // Location filter - show the active location with type
                  ...(entity?.address_postal_code_name && filters.locationType === 'postal_code'
                    ? [
                        {
                          label: t('market.common.filters.postalCode'),
                          values: {
                            value: entity.address_postal_code_name,
                            icon: 'map-pin' as const,
                          },
                        },
                      ]
                    : entity?.municipality_name && filters.locationType === 'city'
                      ? [
                          {
                            label: t('market.common.filters.city'),
                            values: {
                              value: entity.municipality_name,
                              icon: 'building-2' as const,
                            },
                          },
                        ]
                      : entity?.district_name && filters.locationType === 'district'
                        ? [
                            {
                              label: t('market.common.filters.district'),
                              values: {
                                value: entity.district_name,
                                icon: 'map' as const,
                              },
                            },
                          ]
                        : entity?.address_postal_code_name
                          ? [
                              {
                                label: t('market.common.filters.postalCode'),
                                values: {
                                  value: entity.address_postal_code_name,
                                  icon: 'map-pin' as const,
                                },
                              },
                            ]
                          : []),
                  // Property type filter
                  ...(filters.propertyType
                    ? [
                        {
                          label: t('market.common.filters.propertyType'),
                          values: {
                            value: t(
                              `market.common.propertyTypes.${filters.propertyType.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`
                            ),
                            icon: 'home' as const,
                          },
                        },
                      ]
                    : []),
                ] as FilterItem[]
              }
            />
          ) : (
            <FilterSection>
              <FilterBar>
                <LocationFilters>
                  {/* Location Filter Chips */}
                  {entity?.address_postal_code_name && (
                    <FilterChip
                      active={filters.locationType === 'postal_code'}
                      onClick={() => updateLocationType('postal_code')}
                    >
                      <Icon name="map-pin" size="small" />
                      {entity.address_postal_code_name}
                    </FilterChip>
                  )}
                  {entity?.municipality_name && (
                    <FilterChip
                      active={filters.locationType === 'city'}
                      onClick={() => updateLocationType('city')}
                    >
                      <Icon name="building-2" size="small" />
                      {entity.municipality_name}
                    </FilterChip>
                  )}
                  {entity?.district_name && (
                    <FilterChip
                      active={filters.locationType === 'district'}
                      onClick={() => updateLocationType('district')}
                    >
                      <Icon name="map" size="small" />
                      {entity.district_name}
                    </FilterChip>
                  )}

                  {/* Selected location badge */}
                  <LocationBadge>
                    <Icon name="map-pin" size="small" />
                    {getLocationTypeLabel()}: {getLocationDisplay()}
                  </LocationBadge>
                </LocationFilters>

                {/* Advanced Filters Toggle */}
                <AdvancedFiltersToggle
                  $expanded={showAdvancedFilters}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  {showAdvancedFilters ? (
                    <>
                      <Icon name="x" size="small" />
                      {t('market.common.filters.hideFilters')}
                    </>
                  ) : (
                    <>
                      <Icon name="sliders" size="small" />
                      {t('market.common.filters.advancedFilters')}
                    </>
                  )}
                </AdvancedFiltersToggle>
              </FilterBar>

              {/* Advanced Filters Panel */}
              <AdvancedFiltersPanel $expanded={showAdvancedFilters}>
                <AdvancedFiltersHeader>
                  <span className="header-title">
                    <Icon name="home" size="small" />
                    {t('market.common.filters.propertyType')}
                  </span>
                </AdvancedFiltersHeader>
                <AdvancedFilters>
                  <FilterChip
                    active={filters.propertyType === 'house_apartment'}
                    onClick={() => updatePropertyType('house_apartment')}
                  >
                    <Icon name="home" size="small" />
                    {t('market.common.propertyTypes.houseApartment')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'house'}
                    onClick={() => updatePropertyType('house')}
                  >
                    <Icon name="home" size="small" />
                    {t('market.common.propertyTypes.house')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'apartment'}
                    onClick={() => updatePropertyType('apartment')}
                  >
                    <Icon name="building" size="small" />
                    {t('market.common.propertyTypes.apartment')}
                  </FilterChip>
                  <FilterChip
                    active={filters.propertyType === 'commercial'}
                    onClick={() => updatePropertyType('commercial')}
                  >
                    <Icon name="briefcase" size="small" />
                    {t('market.common.propertyTypes.commercial')}
                  </FilterChip>
                </AdvancedFilters>
              </AdvancedFiltersPanel>
            </FilterSection>
          )}

          <MarketSummaryMetrics t={t} />

          <SectionsContainer>
            {purchaseMarketSections.map((section) => (
              <StatsSection
                key={section.id}
                id={section.id}
                title={t(section.titleKey)}
                // description={t(section.descriptionKey)}
                // period={section.period}
                // location={getLocationLabel()}
                // propertyType={getPropertyTypeLabel()}
                // isHidden={!sectionVisibility[section.id]}
                // onToggleVisibility={() => toggleSectionVisibility(section.id)}
              >
                {section.id === 'price-evolution' ? (
                  <PriceEvolution t={t} />
                ) : section.id === 'price-m2-evolution' ? (
                  <PriceM2Evolution t={t} />
                ) : section.id === 'price-per-room' ? (
                  <PricePerRoom t={t} />
                ) : section.id === 'price-m2-per-room' ? (
                  <PriceM2PerRoom t={t} />
                ) : section.id === 'distribution' ? (
                  <Distribution t={t} />
                ) : (
                  <PlaceholderChart>
                    {t('market.common.placeholder.chartPlaceholder', {
                      section: t(section.titleKey),
                    })}
                  </PlaceholderChart>
                )}
              </StatsSection>
            ))}
          </SectionsContainer>
        </AnalysisTabContentContainer>
      </StatsContent>
    </StatsPageContainer>
  )
}

export interface PurchaseMarketSectionProps {
  entity: any
  service: PurchaseMarketService
  /** When true, hides filters for read-only public view */
  readOnly?: boolean
  /** Initial property type filter value (defaults to 'house_apartment') */
  initialPropertyType?: 'house' | 'apartment' | 'house_apartment'
  /** Initial location type filter value (defaults to 'postal_code') */
  initialLocationType?: 'postal_code' | 'city' | 'district'
  /** Called when user changes filters (location type or property type) */
  onFilterChange?: (filters: any) => void
}

export const PurchaseMarketSection: React.FC<PurchaseMarketSectionProps> = ({
  entity,
  service,
  readOnly = false,
  initialPropertyType,
  initialLocationType,
  onFilterChange,
}) => {
  return (
    <PurchaseMarketProvider
      landData={entity}
      service={service}
      initialPropertyType={initialPropertyType}
      initialLocationType={initialLocationType}
      onFilterChange={onFilterChange}
    >
      <PurchaseMarketContent entity={entity} readOnly={readOnly} />
    </PurchaseMarketProvider>
  )
}
